import { useMemo, useState } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { TextareaInput } from "@/components/ui/Field";
import { NativeSelectField } from "@/components/ui/NativeSelect";
import {
  canRespondDocumentBillingQuestions,
  chooseBillingPositionLabel,
  dokumentBillingQuestionIsOpen,
  formatBillingPositionReference,
  matchPositionForTimesheetMaterial,
  mergedChildrenSignature,
  respondDokumentBillingQuestion,
  type DokumentBillingQuestion,
  type DokumentBillingQuestionItem,
} from "@/lib/dokumentBilling";
import { useCurrentUser } from "@/lib/auth";
import { toast } from "@/lib/toast";
import { readGdMeta } from "@/pages/dokumente/gdPositionMeta";
import {
  buildMergedLeistungFromPositions,
  getMaterialMergedChildren,
} from "@/features/timesheetFlow/billingAnswerPositionUtils";
import {
  mergedChildToEditorPosition,
  normalizePositionTyp,
  patchPositionMeta,
  positionDisplayTitle,
  reconcileMergedPositionFromChildren,
  syncLeistungEinzelpreis,
  type EditorPosition,
} from "@/pages/dokumente/documentPositionModel";
import { buildPositionDisplayNumbers } from "@/pages/dokumente/documentPositionLayout";
import type { TimesheetMaterialRecord } from "@/features/timesheetFlow/timesheetFlowTypes";
import type { BillingQuestion } from "@/features/timesheetFlow/timesheetFlowApi";
import { flattenBillingQuestionsToAnswerRows } from "@/features/timesheetFlow/billingQuestionAnswerRows";

type AnsweredEntry = {
  key: string;
  source: "dokument" | "stundennachweis";
  questionText: string;
  answerText: string;
  positionLocalId: string | null;
  positionLabel: string;
  children: EditorPosition[];
  open: boolean;
  questionId?: number;
  itemId?: number;
  materialPositionId?: number | null;
  respondedAt?: string | null;
  isLatestForTarget: boolean;
  needsUpdate: boolean;
};

function childrenFromKalk(kalk: unknown): EditorPosition[] {
  const children = readGdMeta(kalk).merged_children;
  if (!Array.isArray(children) || !children.length) return [];
  return children.map((child) => mergedChildToEditorPosition(child));
}

function currentChildrenOf(pos: EditorPosition | undefined): EditorPosition[] {
  if (!pos) return [];
  return childrenFromKalk(pos.kalkulation_json);
}

function withChildNames(label: string, children: EditorPosition[]): string {
  if (label.includes("·")) return label;
  const names = children
    .map((child) => (child.bezeichnung || "").trim())
    .filter((name) => name && name.toLowerCase() !== "position");
  if (!names.length) return label;
  const extra = names.slice(0, 3).join(", ");
  return label ? `${label} · ${extra}` : extra;
}

function documentPositionLabel(
  item: DokumentBillingQuestionItem,
  pos: EditorPosition | undefined,
  displayNumber: string | null,
  children: EditorPosition[],
): string {
  const live = formatBillingPositionReference({
    displayNumber: displayNumber || item.position_nr,
    bezeichnung: pos?.bezeichnung || item.material_text,
    beschreibung: pos?.beschreibung || item.position_beschreibung,
    menge: pos?.menge ?? item.material_menge,
    einheit: pos?.einheit || item.material_einheit,
    heroNr: pos?.hero_nr || item.hero_nr,
  });
  return withChildNames(chooseBillingPositionLabel(live, item.position_label), children) || "Position";
}

export function DocumentBillingAnswersDialog({
  open,
  onClose,
  dokumentId,
  dokumentQuestions,
  timesheetQuestions,
  timesheetMaterials,
  positions,
  editable,
  zIndexClass,
  onApplyChildren,
  onReloadQuestions,
}: {
  open: boolean;
  onClose: () => void;
  dokumentId: number;
  dokumentQuestions: DokumentBillingQuestion[];
  timesheetQuestions: BillingQuestion[];
  timesheetMaterials: TimesheetMaterialRecord[];
  positions: EditorPosition[];
  editable: boolean;
  zIndexClass?: string;
  onApplyChildren: (
    localId: string,
    children: EditorPosition[],
    options?: { timesheetMaterialPositionId?: number | null },
  ) => void;
  onReloadQuestions: () => void;
}) {
  const { user } = useCurrentUser();
  const canRespond = canRespondDocumentBillingQuestions(user);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [targetOverride, setTargetOverride] = useState<Record<string, string>>({});

  const selectablePositions = useMemo(
    () =>
      positions.filter((p) => {
        const typ = normalizePositionTyp(p.position_typ);
        return typ !== "titel" && typ !== "text";
      }),
    [positions],
  );

  const entries = useMemo(() => {
    const raw: Omit<AnsweredEntry, "isLatestForTarget" | "needsUpdate">[] = [];
    const byId = new Map(
      positions.filter((p) => p.id != null).map((p) => [Number(p.id), p] as const),
    );
    const displayNumbers = buildPositionDisplayNumbers(positions);
    const numberByLocalId = new Map<string, string>();
    positions.forEach((pos, index) => {
      const number = displayNumbers[index];
      if (number) numberByLocalId.set(pos.localId, number);
    });

    for (const q of dokumentQuestions) {
      const items = q.items || [];
      if (!items.length && (q.question_text || q.response_text)) {
        raw.push({
          key: `doc-q-${q.id}`,
          source: "dokument",
          questionText: String(q.question_text || "Allgemeine Rückfrage"),
          answerText: String(q.response_text || ""),
          positionLocalId: null,
          positionLabel: "Allgemein",
          children: [],
          open: dokumentBillingQuestionIsOpen(q),
          questionId: q.id,
          respondedAt: q.responded_at ?? null,
        });
      }
      for (const item of items) {
        const pos = item.position_id != null ? byId.get(Number(item.position_id)) : undefined;
        const children = childrenFromKalk(
          item.position_kalkulation_json || pos?.kalkulation_json,
        );
        const openItem = !(item.response_text || "").trim() && item.price == null;
        raw.push({
          key: `doc-item-${item.id}`,
          source: "dokument",
          questionText: String(item.question_text || "Rückfrage"),
          answerText: String(item.response_text || ""),
          positionLocalId: pos?.localId ?? null,
          positionLabel: documentPositionLabel(
            item,
            pos,
            pos ? numberByLocalId.get(pos.localId) ?? null : null,
            children,
          ),
          children,
          open: openItem,
          questionId: q.id,
          itemId: item.id,
          respondedAt: item.responded_at ?? q.responded_at ?? null,
        });
      }
    }

    const tsRows = flattenBillingQuestionsToAnswerRows(
      timesheetQuestions,
      timesheetMaterials,
    );
    for (const row of tsRows) {
      const material =
        row.materialPositionId != null
          ? timesheetMaterials.find((m) => Number(m.id) === Number(row.materialPositionId))
          : undefined;
      const pos = matchPositionForTimesheetMaterial(
        positions,
        material,
        row.materialPositionId,
      );
      const children = getMaterialMergedChildren(material);
      raw.push({
        key: `ts-${row.key}`,
        source: "stundennachweis",
        questionText: row.questionText || "Rückfrage",
        answerText: row.answerText || "",
        positionLocalId: pos?.localId ?? null,
        positionLabel:
          row.materialLine ||
          (pos
            ? formatBillingPositionReference({
                displayNumber: numberByLocalId.get(pos.localId),
                bezeichnung: pos.bezeichnung,
                beschreibung: pos.beschreibung,
                menge: pos.menge,
                einheit: pos.einheit,
                heroNr: pos.hero_nr,
              })
            : "") ||
          "Stundennachweis",
        children,
        open: row.isOpen,
        materialPositionId: row.materialPositionId ?? null,
        respondedAt: row.respondedAt ?? null,
      });
    }

    const latestKeyByTarget = new Map<string, string>();
    for (const entry of raw) {
      if (entry.open || entry.children.length === 0) continue;
      const targetKey =
        entry.materialPositionId != null
          ? `mat:${entry.materialPositionId}`
          : entry.positionLocalId
            ? `pos:${entry.positionLocalId}`
            : entry.key;
      const prevKey = latestKeyByTarget.get(targetKey);
      if (!prevKey) {
        latestKeyByTarget.set(targetKey, entry.key);
        continue;
      }
      const prev = raw.find((e) => e.key === prevKey);
      const prevAt = Date.parse(String(prev?.respondedAt || "")) || 0;
      const nextAt = Date.parse(String(entry.respondedAt || "")) || 0;
      if (nextAt >= prevAt) latestKeyByTarget.set(targetKey, entry.key);
    }

    return raw.map((entry) => {
      const targetKey =
        entry.materialPositionId != null
          ? `mat:${entry.materialPositionId}`
          : entry.positionLocalId
            ? `pos:${entry.positionLocalId}`
            : entry.key;
      const isLatestForTarget =
        entry.open || entry.children.length === 0
          ? true
          : latestKeyByTarget.get(targetKey) === entry.key;
      const resolvedLocalId = entry.positionLocalId;
      const pos = resolvedLocalId
        ? positions.find((p) => p.localId === resolvedLocalId)
        : undefined;
      const currentSig = mergedChildrenSignature(currentChildrenOf(pos));
      const answerSig = mergedChildrenSignature(entry.children);
      const needsUpdate =
        !entry.open &&
        entry.children.length > 0 &&
        isLatestForTarget &&
        currentSig !== answerSig;
      return { ...entry, isLatestForTarget, needsUpdate };
    });
  }, [dokumentQuestions, timesheetQuestions, timesheetMaterials, positions]);

  const answered = entries.filter((e) => !e.open);
  const openEntries = entries.filter((e) => e.open);

  const resolveTargetLocalId = (entry: AnsweredEntry): string | null => {
    return targetOverride[entry.key] || entry.positionLocalId || null;
  };

  const applyChildren = (entry: AnsweredEntry) => {
    const targetId = resolveTargetLocalId(entry);
    if (!targetId) {
      toast.error("Bitte zuerst die Zielposition auswählen.");
      return;
    }
    if (entry.children.length === 0) {
      toast.error("Keine Unterpositionen zum Übernehmen vorhanden.");
      return;
    }
    onApplyChildren(targetId, entry.children, {
      timesheetMaterialPositionId: entry.materialPositionId,
    });
    toast.success(
      entry.needsUpdate
        ? "Neue Version der Unterpositionen übernommen."
        : "Unterpositionen übernommen.",
    );
  };

  const respondOpen = async (entry: AnsweredEntry) => {
    if (!canRespond || entry.questionId == null) return;
    const text = (drafts[entry.key] || "").trim();
    if (!text) {
      toast.error("Bitte eine Antwort eingeben.");
      return;
    }
    setBusyKey(entry.key);
    try {
      const targetId = resolveTargetLocalId(entry);
      const pos = targetId ? positions.find((p) => p.localId === targetId) : undefined;
      let kalkulation_json: Record<string, unknown> | null = null;
      let einzelpreis_netto: number | undefined;
      if (pos && entry.children.length > 0) {
        const merged = buildMergedLeistungFromPositions(entry.children, pos.bezeichnung);
        const applied = reconcileMergedPositionFromChildren(
          syncLeistungEinzelpreis(
            patchPositionMeta(
              { ...pos, position_typ: "leistung", kalkulation_json: merged.kalkulation_json },
              {
                merged_children: readGdMeta(merged.kalkulation_json).merged_children,
                preisart: "festpreis",
              },
            ),
          ),
        );
        kalkulation_json =
          applied.kalkulation_json && typeof applied.kalkulation_json === "object"
            ? (applied.kalkulation_json as Record<string, unknown>)
            : null;
        einzelpreis_netto = Number(applied.einzelpreis_netto) || undefined;
      } else if (pos?.kalkulation_json && typeof pos.kalkulation_json === "object") {
        kalkulation_json = pos.kalkulation_json as Record<string, unknown>;
      }

      await respondDokumentBillingQuestion(dokumentId, entry.questionId, {
        items: entry.itemId
          ? [
              {
                item_id: entry.itemId,
                response_text: text,
                kalkulation_json,
                einzelpreis_netto,
              },
            ]
          : undefined,
        response_text: entry.itemId ? undefined : text,
      });
      toast.success("Antwort gespeichert.");
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[entry.key];
        return next;
      });
      onReloadQuestions();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Antwort fehlgeschlagen.");
    } finally {
      setBusyKey(null);
    }
  };

  const renderTakeover = (entry: AnsweredEntry) => {
    if (entry.children.length === 0 || !editable) return null;
    if (!entry.isLatestForTarget) {
      return (
        <p className="mt-2 text-xs text-label-tertiary">
          Ältere Antwort — bitte die neueste Version darunter übernehmen.
        </p>
      );
    }

    const targetId = resolveTargetLocalId(entry);
    const needsPicker = !entry.positionLocalId;

    return (
      <div
        className={clsx(
          "mt-3 rounded-ios border p-3",
          entry.needsUpdate
            ? "border-accent/40 bg-accent/5"
            : "border-separator bg-fill-quaternary/30",
        )}
      >
        <p className="text-xs font-medium text-label">
          {entry.needsUpdate
            ? `Neue Version verfügbar (${entry.children.length} Unterpositionen)`
            : `Unterpositionen (${entry.children.length})`}
        </p>
        <ul className="mt-1 space-y-0.5 text-xs text-label-secondary">
          {entry.children.map((child) => (
            <li key={child.localId}>{child.bezeichnung || "Position"}</li>
          ))}
        </ul>
        {needsPicker || !targetId ? (
          <NativeSelectField
            className="mt-2"
            label="Zielposition im Dokument"
            selectSize="sm"
            value={targetOverride[entry.key] || ""}
            onChange={(e) =>
              setTargetOverride((prev) => ({ ...prev, [entry.key]: e.target.value }))
            }
          >
            <option value="">— Position wählen —</option>
            {selectablePositions.map((pos) => (
              <option key={pos.localId} value={pos.localId}>
                {positionDisplayTitle(pos)}
              </option>
            ))}
          </NativeSelectField>
        ) : (
          <p className="mt-1 text-[11px] text-label-tertiary">
            Ziel:{" "}
            {positionDisplayTitle(
              positions.find((p) => p.localId === targetId) || ({ bezeichnung: "Position" } as EditorPosition),
            )}
          </p>
        )}
        <Button
          type="button"
          size="sm"
          className="mt-2"
          variant={entry.needsUpdate ? "primary" : "secondary"}
          onClick={() => applyChildren(entry)}
        >
          {entry.needsUpdate ? "Neue Version übernehmen" : "Unterpositionen übernehmen"}
        </Button>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Rückfragen"
      description="Offene und beantwortete Rückfragen — neue Unterpositions-Versionen direkt in die Dokumentposition übernehmen."
      size="2xl"
      zIndexClass={zIndexClass}
      fullScreenMobile
      footer={
        <Button type="button" variant="secondary" onClick={onClose}>
          Schließen
        </Button>
      }
    >
      <div className="space-y-6">
        {canRespond && openEntries.length > 0 ? (
          <section className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-label-secondary">
              Offen ({openEntries.length})
            </h3>
            {openEntries.map((entry) => (
              <article
                key={entry.key}
                className="rounded-ios-lg border border-amber-500/30 bg-amber-500/5 p-4"
              >
                <p className="text-[11px] font-medium uppercase tracking-wide text-label-tertiary">
                  {entry.source === "dokument" ? "Dokumentposition" : "Stundennachweis"}
                </p>
                <p className="text-sm font-semibold leading-snug text-label">{entry.positionLabel}</p>
                <p className="mt-2 whitespace-pre-wrap text-sm text-label">{entry.questionText}</p>
                {entry.source === "dokument" ? (
                  <div className="mt-3 space-y-2">
                    <TextareaInput
                      label="Antwort"
                      value={drafts[entry.key] || ""}
                      onChange={(e) =>
                        setDrafts((prev) => ({ ...prev, [entry.key]: e.target.value }))
                      }
                      rows={3}
                    />
                    <Button
                      type="button"
                      size="sm"
                      disabled={busyKey === entry.key}
                      loading={busyKey === entry.key}
                      onClick={() => void respondOpen(entry)}
                    >
                      Antwort speichern
                    </Button>
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-label-tertiary">
                    Bitte im Stundennachweis beantworten.
                  </p>
                )}
              </article>
            ))}
          </section>
        ) : null}

        <section className="space-y-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-label-secondary">
            Beantwortet ({answered.length})
          </h3>
          {answered.length === 0 ? (
            <p className="text-sm text-label-secondary">Keine beantworteten Rückfragen.</p>
          ) : (
            answered.map((entry) => (
              <article
                key={entry.key}
                className={clsx(
                  "rounded-ios-lg border p-4",
                  entry.needsUpdate
                    ? "border-accent/35 bg-accent/[0.04]"
                    : "border-separator bg-card-elevated",
                )}
              >
                <div className="flex flex-wrap items-start gap-2">
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-label-tertiary">
                      {entry.source === "dokument" ? "Dokumentposition" : "Stundennachweis"}
                    </p>
                    <p className="text-sm font-semibold leading-snug text-label">
                      {entry.positionLabel}
                    </p>
                  </div>
                  {entry.needsUpdate ? (
                    <span className="rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent">
                      Update verfügbar
                    </span>
                  ) : null}
                  {!entry.isLatestForTarget ? (
                    <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-label-tertiary">
                      Ältere Antwort
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 text-xs text-label-tertiary">Frage</p>
                <p className="whitespace-pre-wrap text-sm text-label">{entry.questionText}</p>
                <p className="mt-2 text-xs text-label-tertiary">Antwort</p>
                <p className="whitespace-pre-wrap text-sm text-label">
                  {entry.answerText || "—"}
                </p>
                {renderTakeover(entry)}
              </article>
            ))
          )}
        </section>
      </div>
    </Dialog>
  );
}
